import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, addDays, subDays, startOfWeek, parseISO, differenceInMinutes } from 'date-fns'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Settings } from 'lucide-react'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useActivitiesByTour, useActivitiesRealtime, useTourAssignments, useMyTourRole } from '@/hooks/useActivities'
import { useSelectedTour } from '@/context/TourContext'
import { useViewMode } from '@/context/ViewModeContext'
import { useArtistViewMode } from '@/hooks/useArtistViewMode'
import { useNow } from '@/hooks/useNow'
import { ActivityTimeline } from '@/components/activities/ActivityTimeline'
import { WeekView } from '@/components/activities/WeekView'
import { Skeleton } from '@/components/ui/skeleton'
import { getLocalDate } from '@/lib/timezones'
import { withViewTransition } from '@/lib/viewTransition'
import { ThemeToggle } from '@/components/ThemeToggle'
import { NotificationToggle } from '@/components/NotificationToggle'
import { OrgSwitcher } from '@/components/OrgSwitcher'
import { UserMenu } from '@/components/UserMenu'
import { ViewModeToggle } from '@/components/ViewModeToggle'
import { ArtistViewToggle } from '@/components/ArtistViewToggle'
import { SwipeableDay } from '@/components/SwipeableDay'
import { getTourStatus } from '@/components/TourStatus'
import { computeArtistView } from '@/lib/artistView'
import type { Activity } from '@/types'

function getTodayStr(): string {
  return format(new Date(), 'yyyy-MM-dd')
}

function getWeekStart(date: Date): string {
  return format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd')
}

export default function TodayPage() {
  const { profile, user } = useAuth()
  const { activeMembership } = useOrg()
  const navigate = useNavigate()
  const { viewMode } = useViewMode()
  const { mode: artistMode } = useArtistViewMode()
  const today = getTodayStr()
  const now = useNow()

  const [weekStart, setWeekStart] = useState<string>(() => getWeekStart(new Date()))

  const { selectedTour: tour, loading: tourLoading } = useSelectedTour()
  const { data: activities = [], isLoading: activitiesLoading } = useActivitiesByTour(tour?.id)
  const { data: assignments } = useTourAssignments(tour?.id)
  const { data: myTourRole } = useMyTourRole(tour?.id, user?.id)
  const effectiveRole = myTourRole ?? activeMembership?.rol_nivel
  const { subscribe } = useActivitiesRealtime(tour?.id)

  useEffect(() => {
    const unsubscribe = subscribe()
    return () => { unsubscribe?.() }
  }, [tour?.id])

  const { todayActivities, todayDimmedIds } = useMemo(() => {
    const inDay = activities.filter(a => getLocalDate(a.start_time_utc, a.venue_timezone) === today)
    const { visible, dimmedIds } = computeArtistView(
      inDay, effectiveRole, artistMode, assignments, user?.id
    )
    return { todayActivities: visible, todayDimmedIds: dimmedIds }
  }, [activities, today, effectiveRole, artistMode, assignments, user?.id])

  const { weekActivities, weekDimmedIds } = useMemo(() => {
    const weekEnd = format(addDays(parseISO(weekStart), 6), 'yyyy-MM-dd')
    const inWeek = activities.filter(a => {
      const d = getLocalDate(a.start_time_utc, a.venue_timezone)
      return d >= weekStart && d <= weekEnd
    })
    const { visible, dimmedIds } = computeArtistView(
      inWeek, effectiveRole, artistMode, assignments, user?.id
    )
    return { weekActivities: visible, weekDimmedIds: dimmedIds }
  }, [activities, weekStart, effectiveRole, artistMode, assignments, user?.id])

  const isLoading = tourLoading || activitiesLoading
  const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd')
  const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd')

  const weekEndStr = format(addDays(parseISO(weekStart), 6), 'yyyy-MM-dd')
  const isCurrentWeek = weekStart === getWeekStart(new Date())

  return (
    <div className="min-h-screen bg-background max-w-lg md:max-w-2xl lg:max-w-6xl mx-auto overflow-x-hidden">
      {/* Sticky header — compact org + actions. Day nav lives in the body
          so it doesn't dominate the chrome on scroll. */}
      <header className="scroll-header sticky top-0 z-10 px-4 lg:px-8 pt-safe">
        <div className="flex items-center justify-between h-14 lg:h-16 gap-2">
          <div className="min-w-0 flex-shrink flex-grow-0">
            <OrgSwitcher />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden md:block">
              <ViewModeToggle />
            </div>
            <ArtistViewToggle role={effectiveRole} />
            <div className="icon-cluster">
              {activeMembership?.rol_nivel === 'A' && (
                <button
                  onClick={() => navigate('/manager')}
                  aria-label="Manager dashboard"
                >
                  <Settings className="w-[18px] h-[18px]" strokeWidth={2} />
                </button>
              )}
              <NotificationToggle />
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="px-4 lg:px-8 pt-2 pb-safe">
        <div className="max-w-2xl mx-auto">
          {viewMode === 'day' ? (
            <DayHero
              dateStr={today}
              isToday
              tour={tour}
              activities={todayActivities}
              now={now}
              onPrev={() => withViewTransition(() => navigate(`/day/${yesterday}`), 'back')}
              onNext={() => withViewTransition(() => navigate(`/day/${tomorrow}`), 'forward')}
            />
          ) : (
            <WeekHero
              weekStart={weekStart}
              weekEndStr={weekEndStr}
              isCurrentWeek={isCurrentWeek}
              tour={tour}
              activities={weekActivities}
              onPrev={() => setWeekStart(format(subDays(parseISO(weekStart), 7), 'yyyy-MM-dd'))}
              onNext={() => setWeekStart(format(addDays(parseISO(weekStart), 7), 'yyyy-MM-dd'))}
              onJumpToCurrent={() => setWeekStart(getWeekStart(new Date()))}
            />
          )}
        </div>

        {viewMode === 'day' && tour && !isLoading ? (
          <SwipeableDay
            dateKey={today}
            onPrev={() => navigate(`/day/${yesterday}`)}
            onNext={() => navigate(`/day/${tomorrow}`)}
          >
            <div className="min-h-[40dvh]">
              <div key={viewMode} className="animate-in fade-in dur-base ease-out-soft">
                {todayActivities.length === 0 ? (
                  <EmptyState
                    icon="✅"
                    title="Nothing scheduled"
                    description={`No activities for today. Enjoy the rest, ${profile?.nombre?.split(' ')[0] ?? 'crew'}.`}
                  />
                ) : (
                  <ActivityTimeline activities={todayActivities} dimmedIds={todayDimmedIds} />
                )}
              </div>
            </div>
          </SwipeableDay>
        ) : (
          <>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div key={viewMode} className="animate-in fade-in dur-base ease-out-soft">
                {!tour ? (
                  <EmptyState icon="🗓️" title="No active tour" description="There's no active tour right now." />
                ) : (
                  <WeekView activities={weekActivities} weekStart={weekStart} dimmedIds={weekDimmedIds} />
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}

interface DayHeroProps {
  dateStr: string
  isToday: boolean
  tour: ReturnType<typeof useSelectedTour>['selectedTour']
  activities: Activity[]
  now: Date
  onPrev: () => void
  onNext: () => void
  onJumpToday?: () => void
}

// Editorial hero for the day view: eyebrow + big title + subtitle + progress.
// Chevrons sit inline with the eyebrow so day-to-day nav stays discoverable
// without taking chrome weight from the sticky header.
export function DayHero({ dateStr, isToday, tour, activities, now, onPrev, onNext, onJumpToday }: DayHeroProps) {
  const date = parseISO(dateStr)
  const dowShort = format(date, 'EEE').toUpperCase()
  const dateShort = format(date, 'MMM d').toUpperCase()
  const titleLine = isToday ? format(date, 'EEEE, MMMM d') : format(date, 'EEEE, MMMM d')

  const tourStatus = tour ? getTourStatus(tour, now) : null
  const showTourDay = tourStatus && tourStatus.phase === 'in-progress'

  const upcoming = activities.filter(a => new Date(a.start_time_utc) > now)
  const nextActivity = upcoming[0]
  const nextInLabel = nextActivity ? formatCompactTimeUntil(nextActivity.start_time_utc, now) : null

  return (
    <div className="today-heading">
      <div className="today-eyebrow">
        <button onClick={onPrev} className="icon-btn" style={{ width: 28, height: 28 }} aria-label="Previous day">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="flex items-center gap-2 flex-1">
          <span>{dowShort} · {dateShort}</span>
          {showTourDay && (
            <>
              <span className="sep" />
              <span>DAY {tourStatus.dayNumber} OF {tourStatus.totalDays}</span>
            </>
          )}
        </span>
        <button onClick={onNext} className="icon-btn" style={{ width: 28, height: 28 }} aria-label="Next day">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <h1 className="today-title">{titleLine}</h1>
      <div className="today-subtitle">
        <span>
          <strong>{upcoming.length}</strong> {isToday ? 'activities ahead' : activities.length === 1 ? 'activity' : 'activities'}
        </span>
        {nextInLabel && (
          <>
            <span className="sep" />
            <span>Next in {nextInLabel}</span>
          </>
        )}
        {!isToday && onJumpToday && (
          <>
            <span className="sep" />
            <button onClick={onJumpToday} className="underline underline-offset-2 hover:text-foreground transition-colors">
              Jump to today
            </button>
          </>
        )}
      </div>

      {tour && tourStatus && tourStatus.phase !== 'upcoming' && (
        <div className="tour-progress">
          <TourProgressSwitch name={tour.nombre} />
          <div className="tp-bar">
            <div className="tp-fill" style={{ '--fill': tourStatus.pct / 100 } as React.CSSProperties} />
          </div>
          <span className="tp-pct mono">{tourStatus.pct}%</span>
        </div>
      )}
    </div>
  )
}

interface WeekHeroProps {
  weekStart: string
  weekEndStr: string
  isCurrentWeek: boolean
  tour: ReturnType<typeof useSelectedTour>['selectedTour']
  activities: Activity[]
  onPrev: () => void
  onNext: () => void
  onJumpToCurrent: () => void
}

function WeekHero({ weekStart, weekEndStr, isCurrentWeek, tour, activities, onPrev, onNext, onJumpToCurrent }: WeekHeroProps) {
  const now = new Date()
  const tourStatus = tour ? getTourStatus(tour, now) : null

  return (
    <div className="today-heading">
      <div className="today-eyebrow">
        <button onClick={onPrev} className="icon-btn" style={{ width: 28, height: 28 }} aria-label="Previous week">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="flex items-center gap-2 flex-1">
          <span>WEEK · {format(parseISO(weekStart), 'MMM d').toUpperCase()} – {format(parseISO(weekEndStr), 'MMM d').toUpperCase()}</span>
        </span>
        <button onClick={onNext} className="icon-btn" style={{ width: 28, height: 28 }} aria-label="Next week">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <h1 className="today-title">
        {isCurrentWeek ? 'This week' : format(parseISO(weekStart), "'Week of' MMM d")}
      </h1>
      <div className="today-subtitle">
        <span>
          <strong>{activities.length}</strong> {activities.length === 1 ? 'activity' : 'activities'}
        </span>
        {!isCurrentWeek && (
          <>
            <span className="sep" />
            <button onClick={onJumpToCurrent} className="underline underline-offset-2 hover:text-foreground transition-colors">
              Jump to this week
            </button>
          </>
        )}
      </div>

      {tour && tourStatus && tourStatus.phase !== 'upcoming' && (
        <div className="tour-progress">
          <TourProgressSwitch name={tour.nombre} />
          <div className="tp-bar">
            <div className="tp-fill" style={{ '--fill': tourStatus.pct / 100 } as React.CSSProperties} />
          </div>
          <span className="tp-pct mono">{tourStatus.pct}%</span>
        </div>
      )}
    </div>
  )
}

// Compact "in Xh Ym" / "in Xm" for the subtitle next-in label.
function formatCompactTimeUntil(iso: string, now: Date): string {
  const target = new Date(iso)
  const mins = differenceInMinutes(target, now)
  if (mins < 0) return ''
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3 max-w-2xl mx-auto">
      {[1, 2, 3].map(i => (
        <div key={i} className="flex gap-3">
          <Skeleton className="w-12 h-4 mt-1" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 lg:py-20 px-4 text-center max-w-sm mx-auto">
      <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-2xl bg-card flex items-center justify-center mb-5 border border-border">
        <span className="text-3xl lg:text-4xl" aria-hidden>{icon}</span>
      </div>
      <h2 className="font-semibold text-base lg:text-lg text-foreground mb-1.5">{title}</h2>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  )
}

export { LoadingSkeleton, EmptyState }

function TourProgressSwitch({ name }: { name: string }) {
  const { tours, selectedTour, setSelectedTourId } = useSelectedTour()
  if (tours.length <= 1) return <span className="tp-label">{name}</span>
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="tp-label inline-flex items-center gap-1.5 outline-none hover:opacity-85 transition-opacity">
          <span className="truncate min-w-0">{name}</span>
          <ChevronDown className="w-3 h-3 shrink-0 opacity-70" strokeWidth={2.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70 font-medium px-3 py-2">
          Switch tour
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {tours.map(t => {
          const isSelected = t.id === selectedTour?.id
          return (
            <DropdownMenuItem
              key={t.id}
              onClick={() => setSelectedTourId(t.id)}
              className="items-start cursor-pointer"
            >
              <div className="w-4 shrink-0 pt-0.5">
                {isSelected && <Check className="w-3.5 h-3.5 text-primary" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{t.nombre}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {format(parseISO(t.fecha_inicio), 'MMM d')} — {format(parseISO(t.fecha_fin), 'MMM d, yyyy')}
                </p>
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
